import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { ChoiceItemList, type ChoiceItemListProps } from '../../molecules/ChoiceItemList';
import { ChoiceItem, ChoiceItemProps } from '../../atoms';
import { ChoiceListSearch } from '../../molecules/ChoiceListSearch/ChoiceListSearch';

type FormData = {
    namegroup: string;
    idsSociete: string[];
};
export type ChoiceListProps = {
    title: string;
    description: string;
    submitButtonLabel: string;
    placeholderLabelSearch?: string;
    listChoice: Array<ChoiceItemListProps>;
    multiple: boolean;
    defaultValues: string[];
    componentAdd?: React.ReactNode;
    onSubmit: (data: FormData) => void;
    search: (string: string) => void;
    onChange?: (selectedValues: Array<string>) => void;
    noSubmitButton?: boolean;
    invisibleCheckbox?: boolean;
};

export const ChoiceList: React.FunctionComponent<ChoiceListProps> = function ({
    title,
    description,
    submitButtonLabel,
    placeholderLabelSearch,
    listChoice,
    multiple,
    defaultValues = [],
    componentAdd = null,
    onSubmit,
    search,
    onChange,
    noSubmitButton = false,
    invisibleCheckbox = false,
}) {
    const [searchValue, setSearchValue] = React.useState<string>('');
    const searchWrapper: (searchString: string) => void = (searchString) => {
        search(searchString);
        setSearchValue(searchString);
    };

    const [selectedValues, setSelectedValues] = useState<Array<string>>(defaultValues);

    const handleChange = (value: string) => {
        setSelectedValues((currentUpdatedValues) => {
            let updatedValues: Array<string>;
            if (currentUpdatedValues.includes(value)) {
                updatedValues = currentUpdatedValues.filter((v) => v !== value); // Déselectionne si déjà sélectionné
            } else if (multiple) {
                updatedValues = [...currentUpdatedValues, value]; // ajout multiple
            } else {
                updatedValues = [value]; // ajout simple
            }
            if (onChange) {
                onChange(updatedValues);
            }
            return updatedValues;
        });
    };
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit({ namegroup: 'selectionsociete', idsSociete: selectedValues });
    };
    const theme = useTheme();

    useEffect(() => {
        setSelectedValues(defaultValues);
    }, [defaultValues]);

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        width: '100%',
                        height: '600px',
                        overflowY: 'scroll',
                    }}
                >
                    <ChoiceListSearch
                        title={title}
                        description={description}
                        placeholderLabelSearch={placeholderLabelSearch}
                        searchValue={searchValue}
                        setSearchValue={searchWrapper}
                    />
                    <Box
                        className="all-list"
                        sx={[
                            {
                                width: '100%',
                                marginBottom: '8px',
                            },
                        ]}
                    >
                        {selectedValues.length !== 0 ? (
                            <>
                                <Typography
                                    sx={{
                                        paddingLeft: '15px',
                                        paddingBottom: '8px',
                                    }}
                                    variant="l2dcaption"
                                    color="text.secondary"
                                >
                                    Société Sélectionnée
                                </Typography>
                                {(
                                    listChoice.reduce(
                                        (
                                            acc: Array<ChoiceItemProps>,
                                            curr: ChoiceItemListProps,
                                        ) => {
                                            acc.push(
                                                ...curr.listChoice.map((innerListChoice) => ({
                                                    ...innerListChoice,
                                                    updateItem: curr.updateItem,
                                                })),
                                            );
                                            return acc;
                                        },
                                        [] as Array<
                                            ChoiceItemProps & {
                                                updateItem?: (value: string) => void;
                                            }
                                        >,
                                    ) as Array<ChoiceItemProps>
                                )
                                    .filter((item) => selectedValues.indexOf(item.value) !== -1)
                                    .map((item) => (
                                        <ChoiceItem
                                            label={item.label}
                                            idFiscal={item.idFiscal}
                                            description={item.description}
                                            onChange={handleChange}
                                            isChecked
                                            sx={{ width: 'calc(100% - 25px)' }}
                                            value={item.value}
                                            isDisabled={false}
                                            updateItem={item.updateItem}
                                            invisibleCheckbox={invisibleCheckbox}
                                        />
                                    ))}
                            </>
                        ) : null}

                        {listChoice.map((choiceList) => (
                            <ChoiceItemList
                                {...choiceList}
                                sx={{ paddingRight: '25px' }}
                                key={choiceList.label}
                                listGroupement={
                                    choiceList.listGroupement ? choiceList.listGroupement : []
                                }
                                multiple={multiple}
                                onChange={handleChange}
                                selectedValues={selectedValues}
                                invisibleCheckbox={invisibleCheckbox}
                            />
                        ))}
                    </Box>
                    {componentAdd}
                </Box>
                {!noSubmitButton ? (
                    <Box
                        className="actions"
                        sx={{
                            width: '100%',
                            height: '50px',
                        }}
                    >
                        <Button
                            type="submit"
                            className="submit-btn"
                            variant="contained"
                            color="primary"
                            data-test-id="submit-create-group"
                            disabled={selectedValues.length === 0}
                            sx={{
                                backgroundColor: theme.palette.primary.dark,
                                width: '100%',
                                marginTop: '30px',
                                padding: '15px',
                                fontSize: '1rem',
                                borderRadius: '15px',
                                transition: 'background-color ease-in-out 300ms',
                                ':hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            {submitButtonLabel || 'Continuer'}
                        </Button>
                    </Box>
                ) : null}
            </form>
        </Box>
    );
};
